import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tags } from 'lucide-react';
import type { MediaRef, TranslatableString } from '@geoic/shared';
import { Button } from '@/components/ui/Button.js';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Input, Select } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { queryKeys } from '@/lib/query-keys.js';
import { dateOnly } from '@/lib/format.js';
import { ContentListPage } from '../ContentListPage.js';
import { ContentEditPage } from '../ContentEditPage.js';
import { createContentApi, type ContentRecord } from '../content-api.js';

export type DocumentRecord = ContentRecord & {
  title: TranslatableString;
  description: TranslatableString;
  file: MediaRef;
  documentNumber: string | null;
  documentDate: string | null;
  categoryId: string | null;
  downloadCount: number;
};

export type DocumentCategoryRecord = ContentRecord & { name: TranslatableString };

export const documentsApi = createContentApi<DocumentRecord>('/documents');
export const documentCategoriesApi =
  createContentApi<DocumentCategoryRecord>('/document-categories');

function useCategoryOptions() {
  const params = { limit: 100, sort: 'title' as const };
  const query = useQuery({
    queryKey: queryKeys.content.list('document-categories', params),
    queryFn: ({ signal }) => documentCategoriesApi.list(params, signal),
    staleTime: 5 * 60 * 1000,
  });

  return (query.data?.data ?? []).map((row) => ({
    value: row._id,
    label: row.name?.uz || row.slug || row._id,
  }));
}

/**
 * Hujjatlar (TZ 7.1).
 *
 * Slug YO'Q — hujjatning alohida sahifasi yo'q, u ro'yxatdan PDF sifatida
 * yuklab olinadi (`SimpleContentConfig.slugField: null`).
 */
export function DocumentsListPage() {
  const categories = useCategoryOptions();
  const byId = new Map(categories.map((option) => [option.value, option.label]));

  return (
    <ContentListPage<DocumentRecord>
      title="Hujjatlar"
      collection="documents"
      contentApi={documentsApi}
      basePath="/documents"
      createLabel="Hujjat qo‘shish"
      sortable
      emptyTitle="Hozircha hujjat yo‘q"
      emptyHint="PDF fayllarni yuklang — ular «Rasmiy hujjatlar» bo‘limida chiqadi."
      actions={
        <Link to="/document-categories">
          <Button variant="outline" icon={<Tags className="size-4" />}>
            Kategoriyalar
          </Button>
        </Link>
      }
      columns={[
        {
          key: 'title',
          header: 'Nomi',
          render: (row) => (
            <span className="font-medium text-n-800">{row.title?.uz || '(nomsiz)'}</span>
          ),
        },
        {
          key: 'category',
          header: 'Kategoriya',
          width: '180px',
          hideOnTablet: true,
          render: (row) => (
            <span className="text-n-600">
              {row.categoryId ? (byId.get(row.categoryId) ?? '—') : '—'}
            </span>
          ),
        },
        {
          key: 'documentDate',
          header: 'Sana',
          width: '120px',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{dateOnly(row.documentDate)}</span>,
        },
        {
          key: 'downloadCount',
          header: 'Yuklab olindi',
          align: 'right',
          width: '120px',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{row.downloadCount ?? 0}</span>,
        },
      ]}
    />
  );
}

type DocumentForm = {
  title: TranslatableString;
  description: TranslatableString;
  file: MediaRef | null;
  documentNumber: string;
  documentDate: string;
  categoryId: string;
};

export function DocumentEditPage() {
  const categories = useCategoryOptions();

  return (
    <ContentEditPage<DocumentForm>
      collection="documents"
      contentApi={documentsApi}
      basePath="/documents"
      backLabel="Hujjatlar"
      newTitle="Yangi hujjat"
      previewable={false}
      titleOf={(form) => form.title.uz}
      empty={() => ({
        title: { uz: '', ru: '', en: '' },
        description: { uz: '', ru: '', en: '' },
        file: null,
        documentNumber: '',
        documentDate: '',
        categoryId: '',
      })}
      fromRecord={(record) => {
        const doc = record as DocumentRecord;
        return {
          title: doc.title ?? { uz: '', ru: '', en: '' },
          description: doc.description ?? { uz: '', ru: '', en: '' },
          file: doc.file ?? null,
          documentNumber: doc.documentNumber ?? '',
          documentDate: doc.documentDate ? doc.documentDate.slice(0, 10) : '',
          categoryId: doc.categoryId ?? '',
        };
      }}
      toInput={(form) => ({
        title: form.title,
        description: form.description,
        file: form.file,
        documentNumber: form.documentNumber || null,
        documentDate: form.documentDate || null,
        categoryId: form.categoryId || null,
      })}
      renderFields={({ form, setForm, fieldErrors }) => (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Hujjat" />
            <CardBody className="space-y-4">
              <TranslatableInput
                label="Nomi"
                required
                value={form.title}
                onChange={(title) => setForm((current) => ({ ...current, title }))}
                error={fieldErrors['title.uz']}
              />
              <TranslatableInput
                label="Tavsif"
                multiline
                rows={3}
                value={form.description}
                onChange={(description) => setForm((current) => ({ ...current, description }))}
              />
              <MediaPicker
                label="PDF fayl"
                kind="file"
                value={form.file}
                onChange={(file) => setForm((current) => ({ ...current, file }))}
                hint="Faqat PDF. Fayl majburiy"
              />
              {fieldErrors.file && <p className="text-sm text-error-600">{fieldErrors.file}</p>}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Rekvizitlar" />
            <CardBody className="space-y-4">
              <Input
                label="Hujjat raqami"
                value={form.documentNumber}
                placeholder="ПКМ-123"
                onChange={(event) =>
                  setForm((current) => ({ ...current, documentNumber: event.target.value }))
                }
              />
              <Input
                type="date"
                label="Hujjat sanasi"
                value={form.documentDate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, documentDate: event.target.value }))
                }
              />
              <Select
                label="Kategoriya"
                value={form.categoryId}
                placeholder="Tanlanmagan"
                options={categories}
                onChange={(event) =>
                  setForm((current) => ({ ...current, categoryId: event.target.value }))
                }
              />
            </CardBody>
          </Card>
        </div>
      )}
    />
  );
}

// ─── Kategoriyalar ───────────────────────────────────────────────────────────

export function DocumentCategoriesListPage() {
  return (
    <ContentListPage<DocumentCategoryRecord>
      title="Hujjat kategoriyalari"
      collection="document-categories"
      contentApi={documentCategoriesApi}
      basePath="/document-categories"
      createLabel="Kategoriya qo‘shish"
      sortable
      emptyTitle="Hozircha kategoriya yo‘q"
      columns={[
        {
          key: 'name',
          header: 'Nomi',
          render: (row) => (
            <span className="font-medium text-n-800">{row.name?.uz || '(nomsiz)'}</span>
          ),
        },
      ]}
    />
  );
}

type CategoryForm = { name: TranslatableString; slug: string };

export function DocumentCategoryEditPage() {
  return (
    <ContentEditPage<CategoryForm>
      collection="document-categories"
      contentApi={documentCategoriesApi}
      basePath="/document-categories"
      backLabel="Kategoriyalar"
      newTitle="Yangi kategoriya"
      previewable={false}
      titleOf={(form) => form.name.uz}
      empty={() => ({ name: { uz: '', ru: '', en: '' }, slug: '' })}
      fromRecord={(record) => ({
        name: (record as DocumentCategoryRecord).name ?? { uz: '', ru: '', en: '' },
        slug: record.slug ?? '',
      })}
      toInput={(form) => ({ name: form.name, slug: form.slug })}
      renderFields={({ form, setForm, fieldErrors }) => (
        <Card className="max-w-2xl">
          <CardHeader title="Kategoriya" />
          <CardBody className="space-y-4">
            <TranslatableInput
              label="Nomi"
              required
              value={form.name}
              onChange={(name) => setForm((current) => ({ ...current, name }))}
              error={fieldErrors['name.uz']}
            />
            <Input
              label="Manzil (slug)"
              value={form.slug}
              onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))}
              error={fieldErrors.slug}
            />
          </CardBody>
        </Card>
      )}
    />
  );
}
